/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { FormEvent } from 'react';
import { useEffect, useRef } from 'react';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import SurveyProgressIndicator from 'bundles/expression-of-interest/components/ddp-multistep/SurveyProgressIndicator';
import SurveyQuestionField from 'bundles/expression-of-interest/components/ddp-multistep/SurveyQuestionField';
import type { SurveyFieldConfig } from 'bundles/expression-of-interest/components/ddp-multistep/SurveyQuestionField';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  heading: string;
  submitLabel: string;
  fields: SurveyFieldConfig[];
  values: Record<string, string>;
  onFieldChange: (fieldId: string, value: string) => void;
  onFieldFocus?: (fieldId: string) => void;
  onSubmit: (submission: { viaKeyboard: boolean }) => void;
  stepNumber: number;
  totalSteps: number;
  focusFirstFieldOnMount?: boolean;
  fieldsOptional?: boolean;
  isSubmitting?: boolean;
  hasSubmitError?: boolean;
  submissionDisclaimer?: string;
};

const styles = {
  heading: css`
    margin-bottom: 0;
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--cds-spacing-150) var(--cds-spacing-200);
    margin: var(--cds-spacing-200) 0;

    ${breakpoints.down('xs')} {
      grid-template-columns: 1fr;
    }
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    flex-wrap: nowrap;

    ${breakpoints.down('xs')} {
      flex-wrap: wrap;
    }
  `,
  caption: css`
    margin-top: var(--cds-spacing-100);
  `,
  submitError: css`
    margin: var(--cds-spacing-150) 0 0;
  `,
  submitButton: css`
    white-space: nowrap;
    flex-shrink: 0;

    ${breakpoints.down('xs')} {
      margin-top: var(--cds-spacing-100);
    }
  `,
};

// Skips hidden inputs, which dropdowns render alongside their visible control.
const FOCUSABLE_FIELD_SELECTOR =
  'input:not([type="hidden"]):not([tabindex="-1"]):not([aria-hidden="true"]), select, textarea, [role="button"]';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isFieldComplete = (field: SurveyFieldConfig, values: Record<string, string>) => {
  if (field.kind === 'phone') {
    return Boolean(field.value?.phoneNumber);
  }
  const value = values[field.id]?.trim();
  if (field.kind === 'email') {
    return Boolean(value && EMAIL_PATTERN.test(value));
  }
  return Boolean(value);
};

const SurveySectionForm = (props: Props) => {
  const {
    heading,
    submitLabel,
    fields,
    values,
    onFieldChange,
    onFieldFocus,
    onSubmit,
    stepNumber,
    totalSteps,
    focusFirstFieldOnMount,
    fieldsOptional,
    isSubmitting,
    hasSubmitError,
    submissionDisclaimer,
  } = props;

  const fieldsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (focusFirstFieldOnMount) {
      fieldsRef.current?.querySelector<HTMLElement>(FOCUSABLE_FIELD_SELECTOR)?.focus();
    }
  }, [focusFirstFieldOnMount]);

  const isSectionComplete = fieldsOptional || fields.every((field) => isFieldComplete(field, values));

  // Mouse clicks report a click count of 1 or more; Enter or Space reports 0.
  const submittedViaKeyboardRef = useRef(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isSectionComplete || isSubmitting) {
      return;
    }
    onSubmit({ viaKeyboard: submittedViaKeyboardRef.current });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography2 component="h3" variant="titleSmall" css={styles.heading}>
        {heading}
      </Typography2>
      <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.caption}>
        {_t('Takes less than a minute. No commitment required.')}
      </Typography2>
      <SurveyProgressIndicator stepNumber={stepNumber} totalSteps={totalSteps} />
      <div css={styles.grid} ref={fieldsRef}>
        {fields.map((field) => {
          if (field.kind === 'phone') {
            const { id, ...phoneProps } = field;
            return (
              <SurveyQuestionField
                key={id}
                {...phoneProps}
                onFocus={(inputName) => onFieldFocus?.(`${id}_${inputName}`)}
              />
            );
          }

          if (field.kind === 'dropdown') {
            return (
              <SurveyQuestionField
                key={field.id}
                kind="dropdown"
                label={field.label}
                placeholder={field.placeholder}
                fullWidth={field.fullWidth}
                searchable={field.searchable}
                optional={fieldsOptional}
                value={values[field.id] ?? ''}
                onChange={(value) => onFieldChange(field.id, value)}
                onOpen={() => onFieldFocus?.(field.id)}
                options={field.options}
              />
            );
          }

          return (
            <SurveyQuestionField
              key={field.id}
              kind={field.kind}
              label={field.label}
              placeholder={field.placeholder}
              fullWidth={field.fullWidth}
              value={values[field.id] ?? ''}
              onChange={(value) => onFieldChange(field.id, value)}
              onFocus={() => onFieldFocus?.(field.id)}
            />
          );
        })}
      </div>
      <div css={styles.actions}>
        <Button
          variant="primary"
          icon={<ArrowNextIcon />}
          iconPosition="after"
          type="submit"
          onClick={(event) => {
            submittedViaKeyboardRef.current = event.detail === 0;
          }}
          disabled={!isSectionComplete || isSubmitting}
          css={styles.submitButton}
        >
          {submitLabel}
        </Button>
        {submissionDisclaimer && (
          <Typography2 component="p" variant="bodyTertiary" color="supportText">
            {submissionDisclaimer}
          </Typography2>
        )}
      </div>
      <Typography2
        component="p"
        variant="bodySecondary"
        color="error"
        role="alert"
        css={hasSubmitError && styles.submitError}
      >
        {hasSubmitError && _t('Something went wrong. Please try again.')}
      </Typography2>
    </form>
  );
};

export default SurveySectionForm;
